import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { hexToHsv, hsvToHex, kelvinToHex } from '@casa/util';
import { CARD_BUTTON, CARD_COLORS, CardButton, PickerCard, stopEvent } from '../../view/picker_card.tsx';
import { SWATCHES, WHITE_RANGE, swatchChange, type LightChange, type LightState } from './light_tile.ts';

// A light's card (docs/specs/light-tile.md LT-11): On and Off, its brightness on a large slider,
// its white tone, and its colour from swatches or a hue strip, each where the light has it. A
// slider sends as it moves, at most every SEND_MS, and once more where it is let go. Done and
// Escape close it.

/** The least time between two commands while a slider moves. */
const SEND_MS = 300;

const clamp01 = (value: number) : number => Math.min(1, Math.max(0, value));

/** The rainbow of the hue strip, at full saturation. */
const HUES = Array.from({ length: 13 }, (_, index) => hsvToHex({ h: index * 30, s: 1, v: 1 })).join(', ');

interface SliderProps {
    label: string;
    /** 0–1 along the slider: up for a vertical one, right for a horizontal one. */
    value: number;
    valueText: string;
    vertical?: boolean;
    style: CSSProperties;
    /** The track, behind the thumb or fill. */
    background: string;
    /** A vertical slider fills from the bottom in this colour; a horizontal one has a thumb. */
    fill?: string;
    /**
     * Shown faded: the light is off, and the level is the one it comes back at; or the light shows
     * the other of a white tone and a colour (the Mac's light tile results, note 2).
     */
    isFaded?: boolean;
    onMove(value: number, isFinal: boolean) : void;
}

/** A slider for a finger: the whole track takes the touch, and the value follows it. */
function Slider({ label, value, valueText, vertical = false, style, background, fill, isFaded = false, onMove }: SliderProps) : ReactNode {
    const track = useRef<HTMLDivElement>(null);
    const at = (event: PointerEvent<HTMLDivElement>) : number => {
        const box = track.current?.getBoundingClientRect();
        if (!box) {
            return value;
        }

        return vertical ? clamp01((box.bottom - event.clientY) / box.height) : clamp01((event.clientX - box.left) / box.width);
    };
    const onKey = (event: KeyboardEvent<HTMLDivElement>) : void => {
        const step = event.key === 'ArrowUp' || event.key === 'ArrowRight' ? 0.05 : event.key === 'ArrowDown' || event.key === 'ArrowLeft' ? -0.05 : 0;
        if (step !== 0) {
            event.preventDefault();
            onMove(clamp01(value + step), true);
        }
    };

    return (
        <div
            ref={track}
            role="slider"
            tabIndex={0}
            aria-label={label}
            aria-orientation={vertical ? 'vertical' : 'horizontal'}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(value * 100)}
            aria-valuetext={valueText}
            onPointerDown={(event) => {
                stopEvent(event);
                event.currentTarget.setPointerCapture(event.pointerId);
                onMove(at(event), false);
            }}
            onPointerMove={(event) => {
                if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                    onMove(at(event), false);
                }
            }}
            onPointerUp={(event) => {
                if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                    event.currentTarget.releasePointerCapture(event.pointerId);
                    onMove(at(event), true);
                }
            }}
            onKeyDown={onKey}
            onClick={stopEvent}
            style={{ position: 'relative', borderRadius: 14, background, touchAction: 'none', cursor: 'pointer', overflow: 'hidden', outlineOffset: 2, ...style }}
        >
            {vertical ? (
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${value * 100}%`, background: fill ?? '#ffffff', opacity: isFaded ? 0.3 : 1 }} />
            ) : (
                <div style={{ position: 'absolute', top: 2, bottom: 2, left: `calc(${value * 100}% - 7px)`, width: 14, borderRadius: 7, border: '3px solid #ffffff', boxShadow: '0 0 4px rgba(0, 0, 0, 0.6)', boxSizing: 'border-box', opacity: isFaded ? 0.3 : 1 }} />
            )}
        </div>
    );
}

/**
 * Sends a slider's changes at most every SEND_MS while it moves, and the last one where it differs
 * from what was sent: a click sends once, not on press and again on release.
 */
function useThrottledSend(send: (change: LightChange) => void) : (change: LightChange, isFinal: boolean) => void {
    const last = useRef({ at: 0, sent: '' });

    return (change, isFinal) => {
        const now = Date.now();
        const key = JSON.stringify(change);
        if ((isFinal && key !== last.current.sent) || (!isFinal && now - last.current.at >= SEND_MS)) {
            last.current = { at: now, sent: key };
            send(change);
        }
    };
}

export interface LightCardProps {
    anchor: DOMRect;
    title: string;
    state: LightState;
    /** A change: shown at once, and sent to the light. */
    onChange(change: LightChange) : void;
    onClose() : void;
}

export function LightCard({ anchor, title, state, onChange, onClose }: LightCardProps) : ReactNode {
    // What the sliders show while they move, ahead of the readings.
    const [level, setLevel] = useState(state.level ?? 100);
    const [kelvin, setKelvin] = useState(state.colorTemperature ?? 2700);
    const [hue, setHue] = useState(state.color ? (hexToHsv(state.color)?.h ?? 0) : 0);
    const send = useThrottledSend(onChange);
    const done = useRef<HTMLButtonElement>(null);
    const kelvinSpan = WHITE_RANGE.max - WHITE_RANGE.min;
    const isOn = state.on === true;
    // Which of the two the light shows, as it says, or as it can only do one: the other's slider is faded.
    const shows = state.mode ?? (state.canWhite && !state.canColor ? 'CT' : state.canColor && !state.canWhite ? 'RGB' : undefined);
    const isWhiteFaded = shows === 'RGB';
    const isColourFaded = shows === 'CT';

    const segment = (label: string, on: boolean) : ReactNode => (
        <button
            type="button"
            aria-pressed={isOn === on}
            onClick={(event) => {
                stopEvent(event);
                onChange({ on });
            }}
            style={{ ...CARD_BUTTON, flex: 1, height: 40, borderRadius: 10, background: isOn === on ? CARD_COLORS.accent : CARD_COLORS.raised, fontSize: 16, fontWeight: isOn === on ? 600 : 400 }}
        >
            {label}
        </button>
    );

    return (
        <PickerCard anchor={anchor} title={title} aside={isOn && state.canDim ? `${Math.round(level)} %` : undefined} label="light" initialFocus={done} onClose={onClose}>
            <div style={{ display: 'flex', gap: 8, margin: '14px 0' }}>
                {segment('On', true)}
                {segment('Off', false)}
            </div>
            <div style={{ display: 'flex', gap: 14, alignItems: 'stretch' }}>
                {state.canDim ? (
                    <Slider
                        label="Brightness"
                        vertical
                        value={level / 100}
                        valueText={`${Math.round(level)} %`}
                        style={{ width: 72, height: 200, flex: 'none' }}
                        background={CARD_COLORS.raised}
                        fill="#f5f5f5"
                        isFaded={!isOn}
                        onMove={(value, isFinal) => {
                            // At least 1 %: the slider dims; Off switches off.
                            const next = Math.max(1, Math.round(value * 100));
                            setLevel(next);
                            send({ level: next }, isFinal);
                        }}
                    />
                ) : null}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14, justifyContent: 'flex-start' }}>
                    {state.canWhite ? (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: CARD_COLORS.muted, marginBottom: 6 }}>
                                <span>Warm</span>
                                <span>Cool</span>
                            </div>
                            <Slider
                                label="White tone"
                                value={(kelvin - WHITE_RANGE.min) / kelvinSpan}
                                valueText={`${Math.round(kelvin)} K${isWhiteFaded ? ', not in use: the light shows a colour' : ''}`}
                                style={{ height: 34 }}
                                isFaded={isWhiteFaded}
                                background={`linear-gradient(to right, ${kelvinToHex(WHITE_RANGE.min)}, ${kelvinToHex(4000)}, ${kelvinToHex(WHITE_RANGE.max)})`}
                                onMove={(value, isFinal) => {
                                    const next = Math.round(WHITE_RANGE.min + value * kelvinSpan);
                                    setKelvin(next);
                                    send({ colorTemperature: next }, isFinal);
                                }}
                            />
                        </div>
                    ) : null}
                    {state.canColor ? (
                        <>
                            <div role="group" aria-label="Colours" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                                {SWATCHES.map((swatch) => (
                                    <button
                                        key={swatch.label}
                                        type="button"
                                        aria-label={swatch.label}
                                        title={swatch.label}
                                        onClick={(event) => {
                                            stopEvent(event);
                                            const change = swatchChange(swatch, state);
                                            // The sliders show where the swatch put the light.
                                            if ('colorTemperature' in change) {
                                                setKelvin(change.colorTemperature);
                                            } else if ('color' in change) {
                                                setHue(hexToHsv(change.color)?.h ?? hue);
                                            }
                                            onChange(change);
                                        }}
                                        style={{ ...CARD_BUTTON, height: 32, borderRadius: 16, background: swatch.color, border: '2px solid rgba(255, 255, 255, 0.25)' }}
                                    />
                                ))}
                            </div>
                            <Slider
                                label="Colour"
                                value={hue / 360}
                                valueText={`${Math.round(hue)}°${isColourFaded ? ', not in use: the light shows a white tone' : ''}`}
                                style={{ height: 34 }}
                                isFaded={isColourFaded}
                                background={`linear-gradient(to right, ${HUES})`}
                                onMove={(value, isFinal) => {
                                    const next = Math.round(value * 360);
                                    setHue(next);
                                    send({ color: hsvToHex({ h: next, s: 1, v: 1 }) }, isFinal);
                                }}
                            />
                        </>
                    ) : null}
                </div>
            </div>
            <div style={{ display: 'flex', marginTop: 16 }}>
                <CardButton label="Done" isMain buttonRef={done} onPress={onClose} />
            </div>
        </PickerCard>
    );
}
